import { useQuery, useMutation } from "@tanstack/react-query";
import { ADMIN_SUPPORT_MODULE_ID } from "constants/constants";

const uploadFileService = async ({ selectedFile, userId, scriptId }) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${userId}&selected_script_id=${scriptId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", selectedFile);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order."
    );
  }
};

export const useUploadFileService = () => {
  return useMutation({ mutationFn: uploadFileService });
};

export const useUploadProductOffering = () => {
  return useMutation({
    mutationFn: (payload) =>
      uploadFile("/api/product-offerings/upload", payload),
    onSuccess: (data) => {
      if (data.signedUrl) {
        window.location.href = data.signedUrl;
      } else {
        alert("Upload successful, but no download link received.");
      }
    },
    onError: (error) => {
      console.error("Error uploading product offering:", error.message);
    },
  });
};

export const useUploadFeatureOffering = () => {
  return useMutation({
    mutationFn: (payload) =>
      uploadFile("/api/feature-offerings/upload", payload),
    onSuccess: (data) => {
      if (data.signedUrl) {
        window.location.href = data.signedUrl;
      } else {
        alert("Upload successful, but no download link received.");
      }
    },
    onError: (error) => {
      console.error("Error uploading feature offering:", error.message);
    },
  });
};

const searchPackages = async (searchTerm) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/search-packages`
  );
  url.searchParams.append("name", searchTerm);

  const headers = {
    "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY,
    accept: "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useSearchPackage = (searchTerm, enabled = false) => {
  return useQuery({
    queryKey: ["searchPackages", searchTerm],
    queryFn: () => searchPackages(searchTerm),
    enabled: !!searchTerm && enabled,
  });
};

const fetchPackageDetails = async (packageGroupId) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-package/${packageGroupId}`
  );

  const headers = {
    "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY,
    accept: "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const usePackageDetails = (packageGroupId) => {
  return useQuery({
    queryKey: ["packageDetails", packageGroupId],
    queryFn: () => fetchPackageDetails(packageGroupId),
    enabled: true,
  });
};

export const EXISTING_VERSION_IDS = [
  { key: 1, label: "V1", value: "V1" },
  { key: 2, label: "V2", value: "V2" },
  { key: 3, label: "V3", value: "V3" },
  { key: 4, label: "V4", value: "V4" },
  { key: 5, label: "V5", value: "V5" },
];

const packageVersion = async (packageId) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-package-version/${packageId}`
  );

  const headers = {
    "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY,
    accept: "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }
  const data = await response.json();
  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const usePackageVersion = (packageId) => {
  return useQuery({
    queryKey: ["packageDetailsByPackageId", packageId],
    queryFn: () => packageVersion(packageId),
    enabled: !!packageId,
  });
};

const createPackageVersion = async ({ packageGroupId, payload }) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/create-package-version/${packageGroupId}`
  );

  const headers = {
    "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY,
    "Content-Type": "application/json",
    accept: "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useCreatePackageVersion = () => {
  return useMutation({ mutationFn: createPackageVersion });
};

const serviceUsageDate = async () => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-service-selling-dates`
  );

  const headers = {
    "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY,
    accept: "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.map((date) => ({
      id: date.id,
      label: `${date.start_usage_date} to ${date.end_usage_date}`,
      start_usage_date: date.start_usage_date,
      end_usage_date: date.end_usage_date,
      value: date.id,
    }));
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useServiceUsageDate = () => {
  return useQuery({
    queryKey: ["serviceUsageDate"],
    queryFn: serviceUsageDate,
    enabled: true,
  });
};

const editPackage = async ({ packageId, payload }) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/edit-package/${packageId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  if (data.status === "success") {
    return data.data;
  }
  throw new Error(
    data.message || "An error occurred while processing the request."
  );
};
export const useEditPackage = () => {
  return useMutation({ mutationFn: editPackage });
};

const fetchHistory = async (user_id) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/get-all-files?uploader_user_id=${user_id}&module_id=${ADMIN_SUPPORT_MODULE_ID}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch history. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders."
    );
  }
};

export const useFetchHistory = (user_id) => {
  return useQuery({
    queryKey: ["history", user_id],
    queryFn: () => fetchHistory(user_id),
  });
};

const fetchProcessedFile = async (fileId) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useFetchProcessedFile = (fileId) => {
  const query = useQuery({
    queryKey: ["processedFile", fileId],
    queryFn: () => fetchProcessedFile(fileId),
    enabled: !!fileId,
  });
  return {
    ...query,
    fetchProcessedFile,
  };
};
